import React, { useState } from "react";
import { Menu, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function AdminSidebar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Menu items (Updated New Service path)
  const menuItems = [
    { name: "Dashboard", icon: "📊", path: "/admin" },
    { name: "Users", icon: "👥", path: "/admin/users" },
    { name: "Orders", icon: "🛒", path: "/admin/orders" },
    { name: "Funds", icon: "💰", path: "/admin/funds" },
    { name: "Reports", icon: "📑", path: "/admin/reports" },
    { name: "New Service", icon: "⚡", path: "/admin/new-service" }, // ✅ Correct
    { name: "Settings", icon: "⚙️", path: "/admin/settings" },
  ];

  const handleNavigate = (path) => {
    setOpen(false);
    setTimeout(() => navigate(path), 250); // ⏳ smooth close + navigate
  };

  return (
    <>
      {/* ☰ Toggle Button */}
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 left-4 z-50 bg-[#0b0f1a]/90 text-white p-2 rounded-lg shadow-lg 
                   backdrop-blur-lg border border-[#1da1f2]/30 hover:scale-110 transition-all duration-300"
        title="Open Admin Menu"
      >
        <Menu size={26} />
      </button>

      {/* 🌌 Overlay (blur background when sidebar open) */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 animate-fadeIn"
        ></div>
      )}

      {/* 🧊 Sidebar */}
      <div
        className={`fixed top-0 left-0 h-full z-50 transition-all duration-500 ease-[cubic-bezier(0.77,0,0.175,1)] transform ${
          open ? "translate-x-0 opacity-100" : "-translate-x-full opacity-0"
        }`}
      >
        <div
          className="w-72 h-full bg-[#0b0f1a]/90 backdrop-blur-2xl border-r border-[#1da1f2]/20 
                     shadow-[0_0_30px_rgba(29,161,242,0.25)] flex flex-col items-center pt-8 relative overflow-hidden"
        >
          {/* ✖ Close Button */}
          <button
            onClick={() => setOpen(false)}
            className="absolute top-5 right-5 text-gray-400 hover:text-white transition-all"
          >
            <X size={26} />
          </button>

          {/* 👑 Admin Profile */}
          <div className="flex flex-col items-center mb-8 mt-4 animate-fadeIn">
            <img
              src="https://cdn-icons-png.flaticon.com/512/3135/3135715.png"
              alt="Admin Avatar"
              className="w-20 h-20 rounded-full border-2 border-[#6c63ff]/40 shadow-[0_0_20px_#1da1f2]"
            />

            {/* ✅ VIP ADMIN + Right-side Badge */}
            <div className="flex items-center justify-center mt-3 gap-1 relative">
              <h2 className="text-xl font-extrabold tracking-wide bg-gradient-to-r from-[#1da1f2] to-[#a855f7] bg-clip-text text-transparent">
                VIP ADMIN
              </h2>

              {/* 🔹 Blue Badge (Right side of text) */}
              <div className="w-5 h-5 bg-gradient-to-br from-[#1da1f2] via-[#6c63ff] to-[#a855f7] 
                              flex items-center justify-center rounded-full shadow-[0_0_10px_#1da1f2]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-3 h-3"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          </div>

          {/* 📋 Menu List */}
          <div className="flex flex-col gap-2 w-full px-6">
            {menuItems.map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={index}
                  onClick={() => handleNavigate(item.path)}
                  className={`flex items-center justify-start gap-4 px-5 py-3 rounded-xl text-base font-semibold transition-all duration-300 hover:scale-[1.03] ${
                    isActive
                      ? "bg-gradient-to-r from-[#1da1f2] to-[#6c63ff] text-white shadow-lg shadow-[#1da1f2]/40"
                      : "bg-[#111827]/60 hover:bg-[#1a1f3a] text-gray-300 hover:text-white"
                  }`}
                >
                  <span className="text-xl">{item.icon}</span>
                  <span>{item.name}</span>

                  {/* ⚡ Special glow for New Service */}
                  {item.name === "New Service" && (
                    <span className="ml-auto text-[#f2a900] animate-pulse text-sm font-bold">
                      NEW✨
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* 🧿 Footer */}
          <div className="absolute bottom-5 text-center text-xs text-gray-500 select-none">
            © 2025{" "}
            <span className="text-[#1da1f2] font-semibold">
              SpeedFollowInfo
            </span>
            .com
          </div>
        </div>
      </div>

      {/* 🎨 Animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease forwards;
        }
        .animate-spin-slow {
          animation: spin 6s linear infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </>
  );
}